/**
 * FormField - Base form input component with label and error handling
 *
 * Features:
 * - Configurable label, placeholder, and input type
 * - Error state styling with message display
 * - Optional right adornment (e.g., for validation checkmarks)
 * - Disabled state support
 */

import React, { forwardRef } from 'react';

interface FormFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Field label */
  label: string;
  /** Error message to display */
  error?: string;
  /** Optional element to render on the right side of the input */
  rightAdornment?: React.ReactNode;
}

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(function FormField({
  label,
  error,
  rightAdornment,
  className = '',
  disabled,
  ...inputProps
}, ref) {
  const hasError = Boolean(error);

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-catch-dark-blue-90">
        {label}
      </label>
      <div className="relative">
        <input
          {...inputProps}
          ref={ref}
          disabled={disabled}
          className={`w-full rounded-lg bg-white px-3.5 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-catch-avocado-400 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 ${
            hasError ? 'ring-2 ring-red-500' : ''
          } ${rightAdornment ? 'pr-10' : ''} ${className}`}
        />
        {rightAdornment && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {rightAdornment}
          </div>
        )}
      </div>
      {hasError && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
});

export default FormField;
